import { DatePipe, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, PrimeTemplate } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { interval, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ToastService } from 'src/app/core/services/toast.service';
import { FilesRequest } from 'src/app/models/FilesRequest';
import { QrCodeDialogComponent } from 'src/app/shared/components/qr-code-dialog/qr-code-dialog.component';
import { STATES_TO_HIDE_CANCEL_OTP_BUTTON, STATES_TO_HIDE_CONTINUE_OTP_BUTTON, SUBSTATES_TO_REOPEN_BELENDER } from 'src/app/shared/constants/common.constants';
import { UtilsService } from 'src/app/utils/utils.service';
import { LoadingComponent } from '../../../utils/loading/loading.component';
import { NewStandardRequestComponent } from '../new-standard-request/new-standard-request.component';
import { NewStandardRequestService } from '../new-standard-request/new-standard-request.service';
import { StandardRequestDetailDocumentsComponent } from './standard-request-detail-documents/standard-request-detail-documents.component';
import { StandardRequestDetailHeaderComponent } from './standard-request-detail-header/request-detail-header.component';
import { StandardRequestDetailService } from './standard-request-detail.service';

@Component({
    selector: 'app-standard-request-detail',
    templateUrl: './standard-request-detail.component.html',
    standalone: true,
    imports: [
        NgIf,
        DrawerModule,
        PrimeTemplate,
        LoadingComponent,
        NewStandardRequestComponent,
        StandardRequestDetailHeaderComponent,
        StandardRequestDetailDocumentsComponent,
        RouterOutlet,
        DatePipe,
        TranslateModule,
        TooltipModule,
        QrCodeDialogComponent
    ]
})
export class StandardRequestDetailComponent implements OnInit, OnDestroy {

    items = [];
    newRequestStatus: {
		title: string, step: number, requestLastStatus?: string
	} = { title: 'New request', step: 1 };

    belenderStatus: {
		actualStatus: string;
		actualStatusMessage: string;
	} = { actualStatus: '', actualStatusMessage: '' };
    consentBelenderUrl: SafeResourceUrl;
    isDownloadZipButtonDisabled = false;

    private restartSyncTimer$ = new Subject<void>();
    private destroy$ = new Subject<void>();

    constructor(
		readonly standardRequestDetailService: StandardRequestDetailService,
        private readonly toastService: ToastService,
		private readonly sanitizer: DomSanitizer,
		private readonly translate: TranslateService,
		private readonly confirmationService: ConfirmationService,
        readonly newStandardRequestService: NewStandardRequestService,
        readonly utilsService: UtilsService
    ) { }

    async ngOnInit(): Promise<void> {
        await this.standardRequestDetailService.getRequestFromUrl();

        this.restartSyncTimer$
            .pipe(
                startWith(void 0),
                switchMap(() => interval(3 * 60 * 1000)),
                takeUntil(this.destroy$)
            )
            .subscribe(async () => await this.sync());
    }

    ngOnDestroy(): void {
        // reset the feasibility when the user navigates away from the request detail page
        this.standardRequestDetailService.resetFeasibilityData();

        this.destroy$.next();
        this.destroy$.complete();
    }

    private async sync(): Promise<void> {
        await this.standardRequestDetailService.refreshRequest(true);
    }

    async syncButton(): Promise<void> {
        await this.sync();
        this.restartSyncTimer$.next();
    }

    reopenOTPWidget(): void {
        try {
            this.standardRequestDetailService.isLoading = true;
            this.newRequestStatus = {
                title: "Continuar con el OTP",
                step: 3,
                requestLastStatus: this.standardRequestDetailService.request.personQuery.substatus
            };
            this.belenderStatus = {
                actualStatus: this.standardRequestDetailService.request.personQuery.substatus,
                actualStatusMessage: this.standardRequestDetailService.request.personQuery.substatus
            };

            this.consentBelenderUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.standardRequestDetailService.request.personQuery.consentUrl);
            this.newStandardRequestService.isFinishingOTP = true;
        } catch (e) {
            console.log(e);
            this.toastService.showError(`Error ${e.status} (${e.statusText})`, `${e.error.detail}`, false);
        } finally {
            this.standardRequestDetailService.isLoading = false;
        }
    }

    async cancelOTP(): Promise<void> {
        try {
            this.standardRequestDetailService.isLoading = true;

            const cancelResponse = await this.standardRequestDetailService.cancelRequest({
                queryId: this.standardRequestDetailService.queryId,
                requestReference: undefined,
                userName: undefined,
                userPassword: undefined
            });

            if (cancelResponse.status !== 'Canceled')
                this.toastService.showError('TOAST.ERROR', `${cancelResponse.status}`, false, 10000);
            else {
                this.toastService.showSuccess('TOAST.SUCCESS', 'NEW_REQUEST.CANCEL', true, 10000);
                await this.standardRequestDetailService.getRequestFromUrl(true);
            }
        } catch (e) {
            this.toastService.showError(`Error ${e.error.status} (${e.error.title})`, `${e.error.detail}`, false);
        } finally {
            this.standardRequestDetailService.isLoading = false;
        }
    }

    confirmCancelOTP(event: Event): void {
        if (!event || !event.target) return;

        this.confirmationService.confirm({
            target: event.target,
            message: this.translate.instant('REQUEST_DETAIL.CANCEL_REQUEST_CONFIRMATION'),
            acceptLabel: this.translate.instant('BUTTON.YES'),
            rejectLabel: this.translate.instant('BUTTON.NO'),
            accept: () => {
                this.cancelOTP().catch(error => { console.error(error); });
            }
        });
    }

    verifyRequestCanBeReopened(): boolean {
        const request = this.standardRequestDetailService.request;
        if (!request) return false;

        const status = request.personQuery.status;
        if (status.toLowerCase() !== 'processing') return false;

        const substatus = request.personQuery.substatus;
        return SUBSTATES_TO_REOPEN_BELENDER.includes(substatus);
    }

    hideContinueOTPButton(): boolean {
        const request = this.standardRequestDetailService.request;
        if (!request) return true;

        const status = request.personQuery.status;
        if (STATES_TO_HIDE_CONTINUE_OTP_BUTTON.includes(status)) return true;

        const substatus = request.personQuery.substatus;
        return !SUBSTATES_TO_REOPEN_BELENDER.includes(substatus);
    }

    hideCancelOTPButton(): boolean {
        const request = this.standardRequestDetailService.request;
        if (!request) return true;

        const status = request.personQuery.status;
        return STATES_TO_HIDE_CANCEL_OTP_BUTTON.includes(status);
    }

    hideDownloadZip(): boolean {
        const documents = this.standardRequestDetailService.request.documents;
        return Object.keys(documents ?? {}).length === 0;
    }

    async downloadZIP(): Promise<void> {
        this.isDownloadZipButtonDisabled = true;
        const req: FilesRequest = {
            detail: this.standardRequestDetailService.request,
            extraFiles: undefined,
            includeJSON: false
        };
        await this.utilsService.downloadZIP(req);
        this.isDownloadZipButtonDisabled = false;
    }
}
